import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { AnimatedProgressBar } from "@/components/Progress";
import { Text } from "@/components/Text";
import { IProfileCompleteness } from "@/data/consultant-profiles";
import { Flex, TStyleSize } from "@/eworkitComponents";

const scoreRange = {
  FRESH: [0, 41],
  GROWING: [41, 82],
  ESTABLISHED: [82, 105],
  PRIME: [105, 116],
};

const levelOrder = ["FRESH", "GROWING", "ESTABLISHED", "PRIME"];

interface ILevelBarProps extends Pick<IProfileCompleteness, "level" | "score"> {
  levelTextSize?: TStyleSize;
}

export const LevelBar: FC<ILevelBarProps> = memo(({ level, score, levelTextSize = "lg" }) => {
  const { t } = useTranslation("session");
  const [startPoints, endPoints] = scoreRange[level];
  let levelIndex = levelOrder.indexOf(level);

  if (levelIndex === levelOrder.length - 1) {
    levelIndex--;
  }

  const levelCompletenessPercentage = 100 / ((endPoints - startPoints) / (score - startPoints));

  return (
    <Flex direction="column" innerPadding="xs">
      <Flex justify="space-between">
        <Text weight="bold" size={levelTextSize}>
          {t(`CONSULTANT_PROFILES.COMPLETENESS.LEVELS.${levelOrder[levelIndex]}`)}
        </Text>
        <Text weight="bold" size={levelTextSize}>
          {t(`CONSULTANT_PROFILES.COMPLETENESS.LEVELS.${levelOrder[++levelIndex]}`)}
        </Text>
      </Flex>
      <Flex grow={1}>
        <AnimatedProgressBar progress={levelCompletenessPercentage} />
      </Flex>
      <Flex justify="space-between">
        <Text color="grey1">{`${startPoints} ${t("CONSULTANT_PROFILES.COMPLETENESS.POINTS")}`}</Text>
        <Text color="grey1">{`${endPoints} ${t("CONSULTANT_PROFILES.COMPLETENESS.POINTS")}`}</Text>
      </Flex>
    </Flex>
  );
});
